/**
 * Which columns a date-shaped view can place its rows on.
 *
 * Mirrors the backend's `can_represent_date`: a date column, the two stamps
 * that subclass it, and a formula-backed column whose result type is a date.
 * The same three-plus-one answer the date filters give, which is not a
 * coincidence -- both ask the field type the same question.
 */
import type { Field, FieldTypeName } from '@/api/types'
import type { DateFieldLike } from '../fields/dates'

const DATE_FIELD_TYPES: readonly FieldTypeName[] = ['date', 'created_on', 'last_modified']

/** Whether this column carries a date the view can read. */
export function representsDate(field: Field): boolean {
  return (
    DATE_FIELD_TYPES.includes(field.type) ||
    (field as { formula_type?: string }).formula_type === 'date'
  )
}

/** The columns worth offering as a view's date column, in the table's order. */
export function dateFields(fields: readonly Field[]): Field[] {
  return fields.filter(representsDate)
}

/**
 * Whether two columns can be the two ends of the same bar.
 *
 * Kept in step with `GanttViewType._date_fields_are_compatible`: the same
 * include-time setting, and -- only when they include time -- the same forced
 * timezone. A bar between two columns that disagree has no length either of
 * them can express, which is why the backend refuses the pair outright.
 *
 * It refuses it on the way in, but only when it is told about both at once.
 * A `PATCH` that names one end alone never reaches the check, so this is the
 * rule the menu applies rather than a second opinion about it.
 */
export function datesAgree(start: DateFieldLike, end: DateFieldLike): boolean {
  const startHasTime = start.date_include_time ?? false
  const endHasTime = end.date_include_time ?? false
  if (startHasTime !== endHasTime) return false
  return (
    !startHasTime ||
    (start.date_force_timezone ?? null) === (end.date_force_timezone ?? null)
  )
}
